import { ApiNotReadyInterceptor } from '@/features/api/apiNotReadyInterceptor'
import AxiosHttpFactory from '@/features/api/axiosHttpFactory'
import { ErrorTransformInterceptor } from '@/features/api/errorTransformInterceptor'
import { MissingNetworkConnectionInterceptor } from '@/features/api/missingNetworkConnectionInterceptor'
import { AuthorizationHeaderInterceptor } from '@/features/api/terminal/authorizationHeaderInterceptor'
import { MissingAuthCredentialInterceptor } from '@/features/api/terminal/missingAuthCredentialInterceptor'
import { ITerminalAPI } from '@/features/api/terminal/types'
import { UnauthedApiInterceptor } from '@/features/api/terminal/unauthedApiInterceptor'
import { oauthAccessToken } from '@/features/auth/oauthAccessToken'
import { DeliveryTime } from '@/features/orders/types/firebase'
import { ReceiptType } from '@/features/print/constants'
import { NotificationPreferences } from '@/features/settings/types'
import { CancelOrderReason } from '@/types/cancelOrder'

class TerminalAPI implements ITerminalAPI {
    private $terminalAPI = AxiosHttpFactory.createMultiClient()

    constructor() {
        new ApiNotReadyInterceptor(
            this.$terminalAPI,
            () => !!this.$terminalAPI.defaults.baseURL && !!oauthAccessToken.get()
        )
        new AuthorizationHeaderInterceptor(this.$terminalAPI, () => oauthAccessToken.get())
        new MissingNetworkConnectionInterceptor(this.$terminalAPI)
        new UnauthedApiInterceptor(this.$terminalAPI)
        new MissingAuthCredentialInterceptor(this.$terminalAPI)
        new ErrorTransformInterceptor(this.$terminalAPI)
    }

    setHostUrl(url: string) {
        this.$terminalAPI.defaults.baseURL = `${url}/api/terminal`
    }

    async acceptOrder(uuid: string, acceptedDeliveryTime: DeliveryTime) {
        await this.$terminalAPI.post('/orders/actions/accept', {
            uuid,
            acceptedDeliveryTime,
        })
    }

    async cancelOrder(
        uuid: string,
        reason: CancelOrderReason,
        returnMoney: boolean,
        note?: string
    ) {
        await this.$terminalAPI.post('/orders/actions/cancel', {
            uuid,
            reason,
            returnMoney,
            ...(note ? { note } : {}),
        })
    }

    async fetchFirebaseCustomToken(): Promise<string> {
        const { data } = await this.$terminalAPI.post<{ customToken: string }>(
            '/firebase-custom-tokens'
        )

        return data.customToken
    }

    async postponeOrder(uuid: string, postponeUntil: string) {
        await this.$terminalAPI.post('/orders/actions/postpone', {
            uuid,
            postponeUntil,
        })
    }

    async startOrderProduction(uuid: string) {
        await this.$terminalAPI.post('/orders/actions/start-production', {
            uuid,
        })
    }

    async finishOrderDelivery(uuid: string) {
        await this.$terminalAPI.post('/orders/actions/finish-delivery', { uuid })
    }

    async finishOrderProduction(uuid: string) {
        await this.$terminalAPI.post('/orders/actions/finish-production', { uuid })
    }

    async startOrderDelivery(uuid: string) {
        await this.$terminalAPI.post('/orders/actions/start-delivery', { uuid })
    }

    async printKitchenBon(
        orderUuid: string,
        lineLength?: number,
        withoutAccents?: boolean
    ): Promise<string> {
        const { data } = await this.$terminalAPI.get<{ printerCommand: string }>(
            '/print-kitchen-bon',
            {
                params: {
                    orderUuid,
                    lineLength,
                    withoutAccents,
                },
            }
        )

        return data.printerCommand
    }

    async printDeliveryTicket(
        orderUuid: string,
        lineLength: number | undefined,
        withoutAccents: boolean | undefined
    ): Promise<string> {
        const { data } = await this.$terminalAPI.get<{ printerCommand: string }>(
            '/print-delivery-ticket',
            {
                params: {
                    orderUuid,
                    lineLength,
                    withoutAccents,
                },
            }
        )

        return data.printerCommand
    }

    async printReceipt(
        orderUuid: string,
        lineLength?: number,
        withoutAccents?: boolean,
        receiptType?: ReceiptType
    ): Promise<string> {
        const { data } = await this.$terminalAPI.get<{ printerCommand: string }>('/print-receipt', {
            params: {
                orderUuid,
                lineLength,
                withoutAccents,
                receiptType,
            },
        })

        return data.printerCommand
    }

    async printOpenTabSubmissionBon(
        submissionUuid: string,
        lineLength?: number,
        withoutAccents?: boolean
    ): Promise<string> {
        const { data } = await this.$terminalAPI.get<{ printerCommand: string }>(
            '/print-open-tab-submission-bon',
            { params: { submissionUuid, lineLength, withoutAccents } }
        )

        return data.printerCommand
    }

    async printShiftCloseout(
        shiftCloseoutUuid: string,
        lineLength?: number,
        withoutAccents?: boolean
    ): Promise<string> {
        const { data } = await this.$terminalAPI.get<{ printerCommand: string }>(
            '/print-shift-closeout',
            { params: { shiftCloseoutUuid, lineLength, withoutAccents } }
        )

        return data.printerCommand
    }

    async fetchNotificationPreferences(consumerUuid: string): Promise<NotificationPreferences> {
        const { data } = await this.$terminalAPI.get('/notification-preferences', {
            params: { consumerUuid },
        })

        return data
    }

    async updateNotificationPreferences(
        consumerUuid: string,
        notificationPreferences: NotificationPreferences
    ) {
        const res = await this.$terminalAPI.post<NotificationPreferences>(
            '/notification-preferences',
            {
                consumerUuid,
                preferences: {
                    ...notificationPreferences,
                },
            }
        )

        return res.data
    }
}

export default new TerminalAPI()
